import { useState, useRef } from "react"
import { useNotificationLogs, useNotificationLogDetail, useResendNotification } from "@/hooks/useNotificationLogs"
import type { NotificationLogItem } from "@/hooks/useNotificationLogs"
import { Card } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import {
  Mail, Send, Eye, ChevronLeft, ChevronRight, Search, X,
  CheckCircle, XCircle, Clock, RefreshCw, Filter,
} from "lucide-react"

const STATUS_MAP: Record<string, { label: string; color: string; icon: any }> = {
  sent: { label: "Enviado", color: "text-green-500", icon: CheckCircle },
  failed: { label: "Falha", color: "text-red-500", icon: XCircle },
  pending: { label: "Pendente", color: "text-amber-500", icon: Clock },
}

const CHANNEL_MAP: Record<string, { label: string; color: string }> = {
  email: { label: "Email", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  push: { label: "Push", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
  in_app: { label: "In-App", color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
}

function formatDate(d?: string) {
  if (!d) return "—"
  return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })
}

export default function NotificationLogs() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [channelFilter, setChannelFilter] = useState("")
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const searchTimer = useRef<number>()

  const { data, isLoading, refetch } = useNotificationLogs({
    page,
    page_size: 15,
    search: search || undefined,
    status: statusFilter || undefined,
    channel: channelFilter || undefined,
  })

  const { data: detail, isLoading: detailLoading } = useNotificationLogDetail(viewingId)
  const resendMut = useResendNotification()

  function handleSearch(val: string) {
    setSearchInput(val)
    clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(() => {
      setSearch(val)
      setPage(1)
    }, 400)
  }

  async function handleResend(logId: string) {
    if (!confirm("Reenviar esta notificação?")) return
    setResendingId(logId)
    try {
      await resendMut.mutateAsync(logId)
      alert("Email reenviado com sucesso!")
    } catch (e: any) {
      alert("Falha ao reenviar: " + (e?.response?.data?.detail || e.message))
    }
    setResendingId(null)
  }

  const logs = data?.items || []
  const total = data?.total || 0
  const pages = data?.pages || 1

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Log de Notificações</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Histórico de todas as notificações enviadas pela plataforma.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400">{total} registros</span>
          <Button variant="ghost" onClick={() => refetch()} className="text-xs">
            <RefreshCw size={14} className="mr-1" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-2.5 text-zinc-400" />
          <input
            type="text"
            placeholder="Buscar por destinatário, assunto..."
            value={searchInput}
            onChange={e => handleSearch(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
          />
          {searchInput && (
            <button onClick={() => { setSearchInput(""); setSearch(""); setPage(1) }} className="absolute right-2.5 top-2.5 text-zinc-400 hover:text-zinc-600">
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
        >
          <option value="">Todos os status</option>
          <option value="sent">Enviados</option>
          <option value="failed">Falhas</option>
          <option value="pending">Pendentes</option>
        </select>
        <select
          value={channelFilter}
          onChange={e => { setChannelFilter(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
        >
          <option value="">Todos os canais</option>
          <option value="email">Email</option>
          <option value="push">Push</option>
          <option value="in_app">In-App</option>
        </select>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
                <th className="text-left px-4 py-3 font-semibold text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Canal</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Tipo</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Destinatário</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Assunto</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Data</th>
                <th className="text-right px-4 py-3 font-semibold text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-400">Carregando...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-400">Nenhuma notificação encontrada.</td></tr>
              ) : (
                logs.map(log => {
                  const st = STATUS_MAP[log.status] || STATUS_MAP.pending
                  const ch = CHANNEL_MAP[log.channel] || CHANNEL_MAP.email
                  const StIcon = st.icon
                  return (
                    <tr key={log.id} className="border-b border-zinc-50 dark:border-zinc-800/50 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1.5 ${st.color}`}>
                          <StIcon size={14} />
                          <span className="text-xs font-medium">{st.label}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${ch.color}`}>{ch.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-zinc-600 dark:text-zinc-300 font-mono">{log.notification_type}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          {log.recipient_name && <span className="text-xs font-medium text-zinc-800 dark:text-zinc-200">{log.recipient_name}</span>}
                          <span className="text-xs text-zinc-500 dark:text-zinc-400">{log.recipient_email || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-zinc-700 dark:text-zinc-300 truncate block max-w-[250px]">{log.subject || "—"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-zinc-500 dark:text-zinc-400 whitespace-nowrap">{formatDate(log.sent_at)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => setViewingId(log.id)}
                            className="p-1.5 rounded text-zinc-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            title="Visualizar"
                          >
                            <Eye size={14} />
                          </button>
                          {log.channel === "email" && (
                            <button
                              onClick={() => handleResend(log.id)}
                              disabled={resendingId === log.id}
                              className="p-1.5 rounded text-zinc-400 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50"
                              title="Reenviar"
                            >
                              <Send size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100 dark:border-zinc-800">
            <span className="text-xs text-zinc-400">
              Página {page} de {pages} ({total} registros)
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded text-zinc-400 hover:text-zinc-600 disabled:opacity-30"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                const p = page <= 3 ? i + 1 : page + i - 2
                if (p < 1 || p > pages) return null
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded text-xs font-medium ${p === page ? "bg-blue-500 text-white" : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"}`}
                  >
                    {p}
                  </button>
                )
              })}
              <button
                onClick={() => setPage(p => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className="p-1.5 rounded text-zinc-400 hover:text-zinc-600 disabled:opacity-30"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* View Modal */}
      {viewingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setViewingId(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800">
              <div>
                <h4 className="text-base font-semibold text-zinc-900 dark:text-white">
                  {detailLoading ? "Carregando..." : detail?.subject || "Notificação"}
                </h4>
                {detail && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Para: {detail.recipient_name} ({detail.recipient_email}) · {formatDate(detail.sent_at)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {detail?.channel === "email" && (
                  <Button onClick={() => handleResend(viewingId)} disabled={resendMut.isPending} className="text-xs">
                    <Send size={12} className="mr-1" /> Reenviar
                  </Button>
                )}
                <button onClick={() => setViewingId(null)} className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-400">
                  <X size={18} />
                </button>
              </div>
            </div>
            {/* Modal body — iframe with HTML preview */}
            <div className="flex-1 overflow-auto bg-zinc-50 dark:bg-zinc-950">
              {detailLoading ? (
                <div className="flex items-center justify-center py-16 text-zinc-400">Carregando...</div>
              ) : detail?.body_html ? (
                <iframe
                  title="Preview da notificação"
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;padding:24px;margin:0;background:#f9fafb;display:flex;justify-content:center;}img{max-width:100%;height:auto;}</style></head><body>${detail.body_html}</body></html>`}
                  className="w-full border-none"
                  style={{ minHeight: "400px", height: "500px" }}
                  sandbox="allow-same-origin"
                />
              ) : (
                <div className="p-6 text-zinc-500 text-sm whitespace-pre-wrap">{detail?.body_text || "Sem conteúdo"}</div>
              )}
            </div>
            {/* Status bar */}
            {detail && (
              <div className="px-6 py-2 border-t border-zinc-100 dark:border-zinc-800 flex items-center gap-4 text-xs text-zinc-400">
                <span>Tipo: <strong className="text-zinc-600 dark:text-zinc-300">{detail.notification_type}</strong></span>
                <span>Canal: <strong className="text-zinc-600 dark:text-zinc-300">{detail.channel}</strong></span>
                <span>Status: <strong className={STATUS_MAP[detail.status]?.color || ""}>{STATUS_MAP[detail.status]?.label || detail.status}</strong></span>
                {detail.error_message && <span className="text-red-500">Erro: {detail.error_message}</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
